import { t, localeTag } from './i18n.js';
import { Billing } from './Billing.js';
import { useEffect, useState } from 'react';
import { api, send } from './api.js';
import { Loading, Notice, formatStorageBytes } from './components.js';
const gb = formatStorageBytes;
const money = (value: number) => `${value.toLocaleString('fr-FR')} F CFA HT`;

/**
 * Decrease scheduled by the website for the next period. The organisation must fit the new
 * capacity on that date, or the change waits and the current subscription stays.
 */
function ScheduledChange({ change, assigned }: { change: any; assigned: number }) {
  const day = new Date(change.effectiveAt).toLocaleDateString(localeTag(), { timeZone: 'UTC' });
  const terms = t('{count} boîte(s), formule {plan}, {price} / {period}', {
    count: change.mailboxes,
    plan: change.plan === 'pro' ? 'Pro' : 'Essential',
    price:
      change.price?.amountExclTax === undefined ? '—' : money(Number(change.price.amountExclTax)),
    period: change.cadence === 'yearly' ? t('an') : t('mois'),
  });
  const excess = assigned - change.mailboxes;
  return (
    <div className="subscription-scheduled">
      <Notice>
        {change.blocked
          ? t(
              'Changement prévu au {date} en attente : {terms}. L’organisation dépasse encore cette capacité ; il s’appliquera dès qu’elle y tiendra.',
              { date: day, terms },
            )
          : t('Changement prévu au {date} : {terms}.', { date: day, terms })}
      </Notice>
      {excess > 0 && (
        <Notice type="error">
          {t(
            '{assigned} boîtes sont attribuées pour {count} prévues : supprimez-en {excess} avant le {date}, sinon le changement restera en attente.',
            { assigned, count: change.mailboxes, excess, date: day },
          )}
        </Notice>
      )}
    </div>
  );
}

/**
 * Ceiling input of one mailbox, in decimal GB, never below the 2 GB floor enforced by the server.
 * Under the storage a paid mailbox brings, it says where the difference goes.
 */
function QuotaInput({
  address,
  current,
  planBytes,
}: {
  address: string;
  current: number;
  planBytes: number;
}) {
  const [value, setValue] = useState(String(current));
  const plan = planBytes / 1e9;
  const wanted = Number(value);
  return (
    <>
      <input
        aria-label={`Plafond en Go pour ${address}`}
        name="quota"
        type="number"
        min="2"
        max="200"
        step="any"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        required
      />
      {plan > 0 && wanted >= 2 && wanted < plan && (
        <small className="quota-warning" role="status">
          {t(
            'Inférieur aux {plan} Go apportés par la formule : les {free} Go libérés restent disponibles pour les autres boîtes.',
            {
              plan: plan.toLocaleString('fr-FR'),
              free: (Math.round((plan - wanted) * 1000) / 1000).toLocaleString('fr-FR'),
            },
          )}
        </small>
      )}
    </>
  );
}

export function Subscription({
  organizationId,
  onQuotaChange,
}: {
  organizationId?: string;
  onQuotaChange?: () => void;
}) {
  const operator = Boolean(organizationId);
  const path = operator
    ? `/platform/organizations/${organizationId}/subscription`
    : '/admin/subscription';
  const [data, setData] = useState<any>();
  const [catalog, setCatalog] = useState<any>();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [units, setUnits] = useState(1);
  const [cadence, setCadence] = useState('monthly');
  const [storage25, setStorage25] = useState(0);
  const [storage100, setStorage100] = useState(0);
  const [reference, setReference] = useState('');
  const [preview, setPreview] = useState<any>();
  const [notice, setNotice] = useState('');
  const load = async () => {
    const [d, c] = await Promise.all([api(path), api('/catalog')]);
    setStorage25(d.subscription?.extensions.storage25 ?? 0);
    setStorage100(d.subscription?.extensions.storage100 ?? 0);
    setData(d);
    // A recorded subscription keeps its edition (guaranteed rate); new ones use the latest.
    setCatalog(c.find((e: any) => e.version === d.subscription?.catalog_version) ?? c[0]);
    setUnits(d.subscription?.units ?? Math.max(1, d.units.assigned));
    setCadence(d.subscription?.cadence ?? 'monthly');
  };
  useEffect(() => {
    setData(undefined);
    setPreview(undefined);
    setReference('');
    setError('');
    void load().catch((e) => setError(e.message));
  }, [path]);
  const act = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fn();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const essential = catalog?.definition.plans.find((plan: any) => plan.id === 'essential');
  // Organisations ordered on the website keep the price agreed there, never the catalogue's.
  const external = data?.billingMode === 'external';
  const agreed =
    data?.subscription?.agreed_amount === null || data?.subscription?.agreed_amount === undefined
      ? null
      : Number(data.subscription.agreed_amount);
  const unitPrice = Number(essential?.[cadence] ?? 0);
  const includedStorage = Number(essential?.bytesPerUnit ?? 0) * units;
  const subscriptionPrice = unitPrice * units;
  const yearlySaving = essential
    ? Number(essential.monthly) * 12 * units - Number(essential.yearly) * units
    : 0;
  const availableExtensions = catalog?.definition.extensions.filter(
    (extension: any) => extension.available,
  );
  return (
    <section className="subscription-panel">
      <h2>{t('Abonnement et stockage commun')}</h2>
      {error && <Notice type="error">{error}</Notice>}
      {notice && <Notice>{notice}</Notice>}
      {!data || !catalog ? (
        <Loading />
      ) : (
        <>
          <header className="subscription-summary-head">
            <div>
              <span className="subscription-eyebrow">
                {data.subscription
                  ? data.subscription.plan === 'pro'
                    ? 'Pro'
                    : 'Essential'
                  : t('Organisation pilote')}
              </span>
              <h3>
                {data.subscription
                  ? (data.subscription.offer_name ??
                    `Vename Mail ${data.subscription.plan === 'pro' ? 'Pro' : 'Essential'}`)
                  : t('Aucun abonnement commercial enregistré.')}
              </h3>
              {data.subscription && (
                <p className="subscription-source">
                  {agreed === null
                    ? t('Catalogue {version} · Révision {revision}', {
                        version: data.subscription.catalog_version,
                        revision: data.subscription.revision,
                      })
                    : t('Prix convenu sur le site Vename · Révision {revision}', {
                        revision: data.subscription.revision,
                      })}
                </p>
              )}
            </div>
            {data.subscription && (
              <div className="subscription-price">
                <strong>
                  {(agreed === null
                    ? data.subscription.unit_price * data.subscription.units +
                      data.subscription.extension_price
                    : agreed
                  ).toLocaleString('fr-FR')}{' '}
                  F CFA
                </strong>
                <span>
                  {data.subscription.cadence === 'monthly' ? t('HT / mois') : t('HT / an')}
                </span>
              </div>
            )}
          </header>
          {data.scheduledChange && (
            <ScheduledChange change={data.scheduledChange} assigned={data.units.assigned} />
          )}
          <div className="subscription-kpis">
            {data.subscription && (
              <article className="subscription-kpi">
                <span className="subscription-kpi-label">{t('Boîtes')}</span>
                <strong className="subscription-kpi-value">
                  {data.units.assigned}
                  <small> / {data.units.subscribed}</small>
                </strong>
                <span className="subscription-kpi-detail">
                  {t('attribuées sur {subscribed} souscrites', {
                    subscribed: data.units.subscribed,
                  })}
                </span>
                <span className={`badge ${data.units.available > 0 ? 'green' : 'orange'}`}>
                  {data.units.available > 0
                    ? data.units.available === 1
                      ? t('1 disponible')
                      : t('{count} disponibles', { count: data.units.available })
                    : t('Aucune unité disponible')}
                </span>
              </article>
            )}
            <article className="subscription-kpi">
              <span className="subscription-kpi-label">{t('Stockage utilisé')}</span>
              <strong className="subscription-kpi-value">{gb(data.storage.usedBytes)}</strong>
              <span className="subscription-kpi-detail">
                {data.storage.limitBytes === null
                  ? t('Pool non activé')
                  : t('sur un pool de {pool}', { pool: gb(data.storage.limitBytes) })}
              </span>
              {data.storage.limitBytes !== null && (
                <progress
                  className={`storage-level-${data.storage.alert}`}
                  max={data.storage.limitBytes}
                  value={Number(data.storage.usedBytes) + Number(data.storage.reservedBytes)}
                  aria-label={t('Stockage utilisé')}
                />
              )}
              {Number(data.storage.reservedBytes) > 0 && (
                <span className="subscription-kpi-detail">
                  {t('Envois réservés : {reserved}', { reserved: gb(data.storage.reservedBytes) })}
                </span>
              )}
            </article>
            <article className="subscription-kpi">
              <span className="subscription-kpi-label">{t('Plafonds attribués')}</span>
              <strong className="subscription-kpi-value">{gb(data.storage.allocatedBytes)}</strong>
              <span className="subscription-kpi-detail">
                {data.storage.unallocatedBytes === null
                  ? '—'
                  : t('{free} disponibles à attribuer', {
                      free: gb(data.storage.unallocatedBytes),
                    })}
              </span>
              {data.storage.limitBytes !== null && (
                <progress
                  className="subscription-allocation"
                  max={data.storage.limitBytes}
                  value={Math.min(
                    Number(data.storage.allocatedBytes),
                    Number(data.storage.limitBytes),
                  )}
                  aria-label={t('Plafonds attribués')}
                />
              )}
            </article>
          </div>
          {data.storage.alert > 0 && (
            <Notice type="info">
              {t(
                'Le stockage atteint {percent} % : libérez de l’espace ou demandez une extension. Aucun dépassement payant automatique.',
                { percent: data.storage.alert },
              )}
            </Notice>
          )}
          <footer className="subscription-summary-foot">
            <small>
              {data.storage.source === 'last_server_reconciliation' &&
                t(
                  'Volumes issus de la dernière synchronisation de chaque boîte avec le serveur mail.',
                )}{' '}
              {t('1 Go = 1 milliard d’octets.')}
            </small>
            <button className="secondary small" disabled={busy} onClick={() => void act(load)}>
              {t('Actualiser')}
            </button>
          </footer>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('Boîte')}</th>
                  <th>{t('Utilisé')}</th>
                  <th>{t('Plafond')}</th>
                  <th>{t('Répartition')}</th>
                </tr>
              </thead>
              <tbody>
                {data.mailboxes.map((box: any) => (
                  <tr key={box.id}>
                    <td>
                      {box.address}
                      <small>
                        {box.status === 'invited'
                          ? t('Invitation en attente · unité réservée')
                          : ''}
                      </small>
                    </td>
                    <td>{gb(box.usedBytes)}</td>
                    <td>
                      {gb(box.quotaBytes)}
                      {Number(box.appliedQuotaBytes) > Number(box.quotaBytes) && (
                        <small>{t('Capacité en attente de libération')}</small>
                      )}
                    </td>
                    <td>
                      {!operator && (
                        <form
                          className="quota-form"
                          onSubmit={(e) => {
                            e.preventDefault();
                            const bytes = Math.round(
                              Number(new FormData(e.currentTarget).get('quota')) * 1e9,
                            );
                            void act(async () => {
                              if (box.kind === 'shared')
                                await send(
                                  `/admin/shared-mailboxes/${box.id}`,
                                  { quotaBytes: bytes, expectedRevision: box.revision },
                                  'PATCH',
                                );
                              else
                                await send(`/users/${box.ownerId}`, { quotaBytes: bytes }, 'PATCH');
                              await load();
                              onQuotaChange?.();
                              setNotice(t('Plafond enregistré.'));
                            });
                          }}
                        >
                          <QuotaInput
                            address={box.address}
                            current={Number(box.quotaBytes) / 1e9}
                            planBytes={
                              box.kind === 'personal'
                                ? Number(data.subscription?.bytes_per_unit ?? 0)
                                : 0
                            }
                          />
                          <button disabled={busy} className="secondary small">
                            {t('Attribuer (Go)')}
                          </button>
                        </form>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p>
            {t(
              'Une unité couvre une boîte nominative. Un alias n’ajoute ni unité ni stockage. Les unités vacantes restent souscrites ; la réduction à échéance sera traitée avec la facturation.',
            )}
          </p>
          {operator && external && (
            <Notice>
              {t('Abonnement tenu par le site Vename : il se modifie par la commande du site.')}
            </Notice>
          )}
          {operator && !external && (
            <form
              className="subscription-form"
              onSubmit={(e) => {
                e.preventDefault();
                void act(async () => {
                  const body = {
                    catalogVersion: catalog.version,
                    plan: 'essential',
                    cadence,
                    units,
                    storage25,
                    storage100,
                    reference,
                    expectedRevision: data.organization.subscription_revision,
                  };
                  const quote = await send(`${path}/preview`, body);
                  setPreview({ quote, body, key: crypto.randomUUID() });
                });
              }}
            >
              <div className="subscription-form-heading">
                <span className="eyebrow">{t('VENAME MAIL ESSENTIAL')}</span>
                <h3>{t('Configurer la souscription')}</h3>
                <p>
                  {t(
                    'Choisissez le nombre de boîtes et la période. Chaque boîte ajoute 5 Go au stockage commun de l’organisation.',
                  )}
                </p>
              </div>

              <fieldset className="subscription-fieldset">
                <legend>{t('Combien de boîtes email faut-il ?')}</legend>
                <p className="field-help">
                  {t('Une unité correspond à une boîte nominative. Les alias sont inclus.')}
                </p>
                <div className="subscription-stepper">
                  <button
                    type="button"
                    aria-label={t('Retirer une boîte')}
                    disabled={units <= Math.max(1, data.units.assigned)}
                    onClick={() => {
                      setUnits((value) => Math.max(Math.max(1, data.units.assigned), value - 1));
                      setPreview(undefined);
                    }}
                  >
                    −
                  </button>
                  <label>
                    <span className="sr-only">{t('Nombre de boîtes email')}</span>
                    <input
                      type="number"
                      min={Math.max(1, data.units.assigned)}
                      max="500"
                      value={units}
                      onChange={(e) => {
                        setUnits(Number(e.target.value));
                        setPreview(undefined);
                      }}
                      required
                    />
                    <small>{t('boîtes')}</small>
                  </label>
                  <button
                    type="button"
                    aria-label={t('Ajouter une boîte')}
                    disabled={units >= 500}
                    onClick={() => {
                      setUnits((value) => Math.min(500, value + 1));
                      setPreview(undefined);
                    }}
                  >
                    +
                  </button>
                </div>
              </fieldset>

              <fieldset className="subscription-fieldset">
                <legend>{t('Choisir la période de facturation')}</legend>
                <div className="subscription-cadence">
                  {(['monthly', 'yearly'] as const).map((value) => (
                    <label className={cadence === value ? 'selected' : ''} key={value}>
                      <input
                        type="radio"
                        name="cadence"
                        value={value}
                        checked={cadence === value}
                        onChange={() => {
                          setCadence(value);
                          setPreview(undefined);
                        }}
                      />
                      <span>
                        <strong>{value === 'monthly' ? t('Mensuelle') : t('Annuelle')}</strong>
                        <small>
                          {money(Number(essential?.[value] ?? 0))} /{' '}
                          {value === 'monthly' ? t('mois') : t('an')}
                        </small>
                        {value === 'yearly' && yearlySaving > 0 && (
                          <em>{t('Économisez {amount}', { amount: money(yearlySaving) })}</em>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>

              {availableExtensions?.length > 0 && (
                <details className="subscription-extras">
                  <summary>{t('Ajouter du stockage')}</summary>
                  {availableExtensions.map((extension: any) => {
                    const current = extension.id === 'storage25' ? storage25 : storage100;
                    return (
                      <label key={extension.id}>
                        {t('{storage} supplémentaires', { storage: gb(extension.bytes) })}
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={current}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            if (extension.id === 'storage25') setStorage25(value);
                            else setStorage100(value);
                            setPreview(undefined);
                          }}
                          required
                        />
                      </label>
                    );
                  })}
                </details>
              )}

              <div className="subscription-live-summary" aria-live="polite">
                <div>
                  <span>{t('Boîtes')}</span>
                  <strong>{units}</strong>
                </div>
                <div>
                  <span>{t('Stockage commun inclus')}</span>
                  <strong>{gb(includedStorage)}</strong>
                </div>
                <div>
                  <span>{t('Total HT')}</span>
                  <strong>{money(subscriptionPrice)}</strong>
                  <small>/ {cadence === 'monthly' ? t('mois') : t('an')}</small>
                </div>
              </div>

              <label className="subscription-reference">
                {t('Référence commerciale')}
                <input
                  value={reference}
                  minLength={3}
                  maxLength={200}
                  placeholder={t('Ex. DEVIS-2026-001')}
                  onChange={(e) => {
                    setReference(e.target.value);
                    setPreview(undefined);
                  }}
                  required
                />
                <small>
                  {t('Numéro du devis, du bon de commande ou du règlement déjà vérifié.')}
                </small>
              </label>
              <button className="primary subscription-review-button" disabled={busy}>
                {busy ? t('Vérification…') : t('Vérifier la souscription')}
              </button>
              {preview && (
                <div className="subscription-confirmation" aria-live="polite">
                  <h4>{t('Confirmer cette souscription')}</h4>
                  <p>{t('Vérifiez le récapitulatif avant d’attribuer les droits de service.')}</p>
                  <dl>
                    <div>
                      <dt>{t('Offre')}</dt>
                      <dd>Vename Mail Essential</dd>
                    </div>
                    <div>
                      <dt>{t('Boîtes email')}</dt>
                      <dd>{units}</dd>
                    </div>
                    <div>
                      <dt>{t('Stockage commun')}</dt>
                      <dd>{gb(preview.quote.storageBytes)}</dd>
                    </div>
                    <div>
                      <dt>{t('Montant HT')}</dt>
                      <dd>
                        {money(preview.quote.amountHT)} /{' '}
                        {cadence === 'monthly' ? t('mois') : t('an')}
                      </dd>
                    </div>
                  </dl>
                  {preview.quote.billing && (
                    <p>
                      {t('Complément pour la période restante : {amount}.', {
                        amount: money(preview.quote.billing.amountHT),
                      })}{' '}
                      {preview.quote.billing.prepaymentRequired
                        ? preview.quote.billing.paid
                          ? t('Complément réglé.')
                          : t('Règlement requis avant activation.')
                        : t('Régularisation sur la prochaine facture.')}
                    </p>
                  )}
                  {preview.quote.billing?.prepaymentRequired && !preview.quote.billing.paid ? (
                    <button
                      type="button"
                      className="primary"
                      disabled={busy}
                      onClick={() =>
                        void act(async () => {
                          await send(
                            `/platform/organizations/${organizationId}/billing/prepare-change`,
                            preview.body,
                          );
                          setPreview(undefined);
                          await load();
                          setNotice(
                            'Complément anticipé émis. Enregistrez le règlement vérifié puis prévisualisez la même modification pour l’activer.',
                          );
                        })
                      }
                    >
                      {t('Émettre le complément anticipé')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="primary"
                      disabled={busy}
                      onClick={() =>
                        void act(async () => {
                          await send(path, preview.body, 'POST', {
                            'Idempotency-Key': preview.key,
                          });
                          setPreview(undefined);
                          await load();
                          setNotice(t('Souscription enregistrée, sans traitement de paiement.'));
                        })
                      }
                    >
                      {t('Confirmer la souscription')}
                    </button>
                  )}
                  <small>
                    {t(
                      'Cette confirmation active le service. Aucun moyen de paiement ne sera débité depuis cet écran.',
                    )}
                  </small>
                </div>
              )}
            </form>
          )}
          <Billing
            organizationId={organizationId}
            subscription={data.subscription}
            catalog={catalog}
            onChange={load}
          />
          <details>
            <summary>{t('Historique commercial')}</summary>
            <ul>
              {data.history.map((r: any) => (
                <li key={r.revision}>
                  {t('Révision {revision} · {units} unités · Catalogue {version} ·', {
                    revision: r.revision,
                    units: r.units,
                    version: r.catalog_version,
                  })}{' '}
                  {r.reference}
                </li>
              ))}
            </ul>
          </details>
        </>
      )}
    </section>
  );
}
export function PlatformSubscriptions({ organizations }: { organizations: any[] }) {
  const [id, setId] = useState('');
  return (
    <section className="admin-card">
      <div className="subscription-panel">
        <h2>{t('Souscriptions des organisations')}</h2>
        <label>
          Organisation
          <select aria-label={t('Organisation')} value={id} onChange={(e) => setId(e.target.value)}>
            <option value="">{t('Choisir une organisation')}</option>
            {organizations.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      {id && <Subscription key={id} organizationId={id} />}
    </section>
  );
}
